'use client'

import dynamic from 'next/dynamic'
import { PropsWithChildren, memo } from 'react'
import NotificationComponent from '@/components/Notification'

// libs
import { Container } from '@/libs/react-components'
import { Main } from './styled'
import { TSeoKeywordData, HeaderProps } from '@microfrontends/app-shell-v2'
import JobDetailProvider from '@/app-context'
import { EJobAnalysisType, ELanguage } from '@/common'
import { TUser } from '@/services'
import {
    EJobInvitationStatus,
    IJobDetail,
    TJobInvitation,
    TSpecialJob
} from '@/services/jobs/type'
import { TNestedGenericId, useIdentify } from '@microfrontends/react-components'
import { TAiCVReview } from '@/services/ai-review'
import { TAITrial } from '@/services/ai-review/types'

const Header = dynamic(() => import('@/components/header'))
const Footer = dynamic(() => import('@/components/footer'))

const msBaseUrl = process.env.NEXT_PUBLIC_MICROSERVICE_DOMAIN ?? ''

type TBaseLayout = {
    jobFunction?: TNestedGenericId
    companyIndustries?: number
    seoKeywords?: TSeoKeywordData[]
    langCode: ELanguage
    user: Partial<TUser> | null
    jobDetail?: Partial<IJobDetail>
    userType?: EJobAnalysisType
    jobInvitation?: TJobInvitation
    specialJob?: TSpecialJob
    cvReviews?: TAiCVReview[]
    cvReviewTrial?: TAITrial | null
    topBannerData?: HeaderProps['topBannerData']
    forceShowTopBanner?: boolean
    containerFull?: boolean
    hiddenFooter?: boolean
}

function BaseLayout(props: PropsWithChildren<TBaseLayout>) {
    useIdentify(props.user, msBaseUrl)
    
    return (
        <JobDetailProvider
            langCode={props.langCode}
            user={props.user}
            jobDetail={props.jobDetail || {}}
            userType={props.userType || EJobAnalysisType.NONE}
            jobInvitation={
                props.jobInvitation ?? {
                    jobInvitationId: -1,
                    status: EJobInvitationStatus.REJECTED,
                    content: ''
                }
            }
            specialJob={props.specialJob}
            cvReviews={props.cvReviews}
            cvReviewTrial={props.cvReviewTrial}
            topBannerData={props.topBannerData}
        >
            {props.topBannerData && props.topBannerData?.length > 0 ? (
                <link
                    rel='preload'
                    as='image'
                    fetchPriority='high'
                    href={
                        props.topBannerData && props.topBannerData?.length > 0
                            ? props.topBannerData[0].attributes.image
                            : ''
                    }
                />
            ) : null}
            <Main>
                <Header
                    jobFunction={props.jobFunction}
                    companyIndustries={props.companyIndustries}
                    forceShowTopBanner={props.forceShowTopBanner}
                />
                <Container full={props.containerFull}>
                    {props.children}
                </Container>
                {!props.hiddenFooter ? (
                    <Footer seoKeywords={props.seoKeywords || []} />
                ) : null}
            </Main>
            <NotificationComponent
                langCode={props.langCode}
                user={props.user as TUser}
            />
        </JobDetailProvider>
    )
}
export default memo(BaseLayout)
